/**
 * Client Health Score Widget
 * ===========================
 * Aggregates all KPIs into a single 0-100 health score per client
 * Shows score breakdown by category with trend indicators
 */

import React from 'react';
import { Activity, TrendingUp, TrendingDown, DollarSign, Users, Shield, Zap, Database } from 'lucide-react';

// Score thresholds
const getScoreConfig = (score) => {
  if (score >= 80) return { label: 'Excellent', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-500', gradient: 'from-emerald-500 to-[#0066CC]', fillColor: '#10b981' };
  if (score >= 60) return { label: 'Good', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', ring: 'ring-blue-500', gradient: 'from-blue-500 to-indigo-500', fillColor: '#3b82f6' };
  if (score >= 40) return { label: 'Needs Attention', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', ring: 'ring-amber-500', gradient: 'from-amber-500 to-orange-500', fillColor: '#f59e0b' };
  return { label: 'Critical', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', ring: 'ring-red-500', gradient: 'from-red-500 to-rose-500', fillColor: '#ef4444' };
};

// Industry-specific health score calculations
const HEALTH_DIMENSIONS = {
  hospitality: [
    { id: 'financial', label: 'Financial Health', icon: DollarSign, weight: 0.30, score: 72, trend: 5, factors: ['Net margin vs benchmark', 'Cash flow stability', 'Revenue growth trend'], sources: ['Xero', 'Square POS'] },
    { id: 'operational', label: 'Operations', icon: Activity, weight: 0.25, score: 68, trend: -2, factors: ['Labour cost %', 'Food cost %', 'Table turnover rate'], sources: ['Deputy', 'Square POS'] },
    { id: 'customer', label: 'Customer', icon: Users, weight: 0.20, score: 85, trend: 8, factors: ['Google review rating', 'Repeat customer rate', 'Avg spend per head'], sources: ['Google Reviews', 'Square POS'] },
    { id: 'compliance', label: 'Compliance', icon: Shield, weight: 0.15, score: 92, trend: 0, factors: ['RSA currency', 'Food safety audit', 'Training completion'], sources: ['Internal Tracking'] },
    { id: 'growth', label: 'Growth', icon: TrendingUp, weight: 0.10, score: 65, trend: 12, factors: ['Revenue YoY growth', 'New customer acquisition', 'Marketing ROI'], sources: ['Xero', 'Google Analytics'] },
  ],
  retail: [
    { id: 'financial', label: 'Financial Health', icon: DollarSign, weight: 0.30, score: 65, trend: 3, factors: ['Gross margin', 'Cash flow', 'Revenue vs target'], sources: ['Xero', 'Square POS'] },
    { id: 'operational', label: 'Operations', icon: Activity, weight: 0.25, score: 58, trend: -5, factors: ['Conversion rate', 'Stock turnover', 'Shrinkage rate'], sources: ['POS', 'Inventory System'] },
    { id: 'customer', label: 'Customer', icon: Users, weight: 0.20, score: 72, trend: 4, factors: ['Customer retention', 'ATV trend', 'NPS score'], sources: ['POS', 'Customer Surveys'] },
    { id: 'compliance', label: 'Compliance', icon: Shield, weight: 0.15, score: 88, trend: 0, factors: ['PCI compliance', 'Consumer law', 'Safety standards'], sources: ['Internal Tracking'] },
    { id: 'growth', label: 'Growth', icon: TrendingUp, weight: 0.10, score: 55, trend: 8, factors: ['Sales growth YoY', 'New customers', 'Online channel growth'], sources: ['Xero', 'Shopify'] },
  ],
  professional: [
    { id: 'financial', label: 'Financial Health', icon: DollarSign, weight: 0.30, score: 78, trend: 5, factors: ['Revenue/partner', 'Recovery rate', 'Cash flow'], sources: ['Practice Management', 'Xero'] },
    { id: 'operational', label: 'Operations', icon: Activity, weight: 0.25, score: 62, trend: -3, factors: ['Utilisation rate', 'Lock-up days', 'WIP management'], sources: ['Time Tracking', 'Practice Management'] },
    { id: 'customer', label: 'Client Relations', icon: Users, weight: 0.20, score: 88, trend: 2, factors: ['Client retention', 'NPS score', 'Referral rate'], sources: ['CRM', 'Client Surveys'] },
    { id: 'compliance', label: 'Compliance', icon: Shield, weight: 0.15, score: 95, trend: 0, factors: ['Professional indemnity', 'CPD completion', 'Regulatory compliance'], sources: ['Internal Tracking'] },
    { id: 'growth', label: 'Growth', icon: TrendingUp, weight: 0.10, score: 70, trend: 15, factors: ['Pipeline value', 'Pitch win rate', 'Advisory revenue growth'], sources: ['CRM'] },
  ],
};

// Circular progress component
const CircularScore = ({ score, size = 120, strokeWidth = 8 }) => {
  const config = getScoreConfig(score);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
        <circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={config.fillColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${config.color}`}>{score}</span>
        <span className="text-[10px] text-gray-500 font-medium">/100</span>
      </div>
    </div>
  );
};

export const ClientHealthScore = ({ industry = 'hospitality', compact = false, className = '' }) => {
  const dimensions = HEALTH_DIMENSIONS[industry] || HEALTH_DIMENSIONS.hospitality;
  const overallScore = Math.round(dimensions.reduce((sum, d) => sum + (d.score * d.weight), 0));
  const overallConfig = getScoreConfig(overallScore);
  const overallTrend = Math.round(dimensions.reduce((sum, d) => sum + (d.trend * d.weight), 0) * 10) / 10;
  
  if (compact) {
    return (
      <div className={`flex items-center gap-3 ${className}`} data-testid="health-score-compact">
        <CircularScore score={overallScore} size={48} strokeWidth={4} />
        <div>
          <p className={`text-sm font-bold ${overallConfig.color}`}>{overallConfig.label}</p>
          <p className="text-[10px] text-gray-500">Business Health</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 ${className}`} data-testid="health-score-widget">
      <div className="absolute -top-20 -right-20 w-48 h-48 bg-gradient-to-br from-indigo-100/40 to-violet-200/30 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${overallConfig.gradient} flex items-center justify-center shadow-lg`}>
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 tracking-tight">Business Health Score</h3>
            <p className="text-xs text-gray-500">Aggregated from all connected data sources</p>
          </div>
        </div>

        {/* Score Circle + Summary */}
        <div className="flex items-center gap-6 mb-6">
          <CircularScore score={overallScore} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-lg font-bold ${overallConfig.color}`}>{overallConfig.label}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                overallTrend >= 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {overallTrend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {overallTrend >= 0 ? '+' : ''}{overallTrend} pts
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Based on {dimensions.length} key business dimensions weighted by impact on overall performance.
            </p>
          </div>
        </div>

        {/* Dimension Breakdown */}
        <div className="space-y-2.5">
          {dimensions.map(dim => {
            const config = getScoreConfig(dim.score);
            const DimIcon = dim.icon;
            return (
              <div key={dim.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50/60 border border-gray-100/50">
                <DimIcon className={`w-4 h-4 ${config.color} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700 truncate">{dim.label}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold ${config.color}`}>{dim.score}/100</span>
                      {dim.trend !== 0 && (
                        <span className={`text-[10px] ${dim.trend > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {dim.trend > 0 ? '+' : ''}{dim.trend}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Score bar */}
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${dim.score}%`, backgroundColor: config.fillColor }} />
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <Database className="w-2.5 h-2.5 text-gray-400" />
                    <span className="text-[9px] text-gray-400">{dim.sources.join(' + ')}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ClientHealthScore;
